import { Json } from '@ff-os/shared-json/Json';
import { logError } from '@ff-os/shared-utils/logging';
import { urls } from '@ff-os/shared-utils/urls';
import { groupBy, uniqBy } from 'lodash';
import { Font, FontFamily, FontStyle, overwrite } from '../../slices/fontsSlice';
import { AppThunkApi } from '../../store';
import { createAsyncAppThunk } from '../createAsyncAppThunk';

export const DEFAULT_FONT_FAMILY: FontFamily = {
    name: 'Open Sans',
    fonts: [
        {
            url: 'https://gift-editor-functions.azurewebsites.net/api/public-assets/340ac5b5-7fdf-4899-9a4d-0d0732794099',
            style: 'regular'
        },
        {
            url: 'https://gift-editor-functions.azurewebsites.net/api/public-assets/665ee481-cee2-4b7c-920d-50e3c0a4230f',
            style: 'bold'
        },
        {
            url: 'https://gift-editor-functions.azurewebsites.net/api/public-assets/1573474f-8bf2-4f87-9d1f-ecd214a36fb0',
            style: 'bolditalic'
        },
        {
            url: 'https://gift-editor-functions.azurewebsites.net/api/public-assets/c3698a2e-3804-4800-885c-02012d9b28c8',
            style: 'italic'
        }
    ]
};

const globalFontCollectionId = '4D773460-5CEA-4587-8167-2D9F5E0D407C';

type FontAssetMeta = { fontFamily: string; fontStyle: FontStyle };
type FontCollection = Json.Collection<FontAssetMeta>;

export const loadFonts = createAsyncAppThunk<void, void>(
    'fonts/loadFont',
    async (_, { dispatch, getState }: AppThunkApi) => {

        const state = getState();
        const families = await loadFontFamilies(
            state.scene.fontCollectionId ?? globalFontCollectionId);

        dispatch(overwrite(families));

        for (const family of families) {
            for (const font of family.fonts) {

                const fontFace = toDOMFontFace(family.name, font);

                try {
                    document.fonts.add(fontFace);
                    await fontFace.load();
                } catch (e) {
                    logError(e);
                }
            }
        }
    });

const loadFontFamilies = async (collectionId: string) => {

    const response = await fetch(
        urls.loadCollection(collectionId),
        { method: 'GET', mode: 'cors' });

    const fontCollection = await response.json() as FontCollection;
    const groupedFonts = groupBy(fontCollection.entries, f => f.meta.fontFamily);

    return Object.entries(groupedFonts).map(([family, fonts]) => ({
        name: family,
        fonts: uniqBy(fonts, f => f.meta.fontStyle).map(f => ({
            url: f.url,
            style: f.meta.fontStyle
        }))
    }));
};

const toDOMFontFace = (family: string, font: Font) => {
    return new FontFace(family, `url(${font.url})`, {
        ...(font.style.includes('bold') ? { weight: 'bold' } : {}),
        ...(font.style.includes('italic') ? { style: 'italic' } : {})
    });
};